<script setup>
import { onBeforeMount, ref, toRefs, watch } from 'vue'

import BaseFileUpload from '@/src/vueComponent/BaseFileUpload.vue'
import BaseSelect from '@/src/vueComponent/BaseSelect.vue'
import BackButton from '@/src/vueComponent/BackButton.vue'
import { SendOutlined } from '@ant-design/icons-vue'

import mySwal from '@/src/javascripts/plugins/mySwal.js'

const props = defineProps({
  currentUser: {
    type: Object,
    default: () => {}
  },
  documentShowInfo: {
    type: Object,
    default: () => {}
  },
  unitList: {
    type: Array,
    default: () => []
  },
  totalReportFileList: {
    type: Array,
    default: () => []
  }
})

const { currentUser, unitList, totalReportFileList } = toRefs(props)
const emit = defineEmits(['submitForm'])

const documentData = ref({
  sign_action: '',
  suggestion: '',
  target: [
    {
      signer_id: null,
      unit_id: null,
      signer_list: []
    }
  ],
  upload_files: []
})

const transformedUnitList = ref([])
onBeforeMount(() => {
  transformedUnitList.value = unitList.value.map((unit) => ({
    label: unit.name,
    value: unit.id,
    users: unit.users.map((user) => ({
      label: user.name,
      value: user.id
    }))
  }))
})

watch(
  () => documentData.value.sign_action,
  (newValue) => {
    if (['forward', 'branch', 'referral'].includes(newValue)) {
      documentData.value.target = [
        {
          signer_id: null,
          step_name: null
        }
      ]
    } else {
      documentData.value.target = []
    }
  }
)

const getUnitStaffList = (event, rowIndex) => {
  documentData.value.target[rowIndex].step_name = event
  documentData.value.target[rowIndex].signer_id = null

  documentData.value.target[rowIndex].signer_list = transformedUnitList.value.find((unit) => {
    return unit.value === event
  }).users
}

const checkUnitStaff = (event, rowIndex) => {
  documentData.value.target[rowIndex].signer_id = event

  if (![null, '', undefined].includes(event)) {
    documentData.value.target.forEach((item, index) => {
      if (rowIndex !== index) {
        if (item.signer_id === event) {
          mySwal.fire({
            title: '收件人重複，請重新選擇'
          })
          documentData.value.target[rowIndex].signer_id = null
        }
      }
    })
  }
}

const addTarget = () => {
  documentData.value = {
    ...documentData.value,
    target: [
      ...documentData.value.target,
      {
        signer_id: null,
        step_name: ''
      }
    ]
  }
}

const deleteTarget = (rowIndex) => {
  documentData.value = {
    ...documentData.value,
    target: [
      ...documentData.value.target.slice(0, rowIndex),
      ...documentData.value.target.slice(rowIndex + 1)
    ]
  }
}

const handleDeletePreviewFile = (fileIndex) => {
  documentData.value = {
    ...documentData.value,
    upload_files: [
      ...documentData.value.upload_files.slice(0, fileIndex),
      ...documentData.value.upload_files.slice(fileIndex + 1)
    ]
  }
}

const submitForm = (actiontype) => {
  if (actiontype === 'cancel') {
    documentData.value = { sign_action: 'cancel' }
  } else if (!documentData.value.sign_action) {
    mySwal.fire({
      title: '請選擇回覆動作'
    })
    return
  } else {
    documentData.value.target = documentData.value.target.map(({ signer_list, ...rest }) => rest)
  }

  emit('submitForm', { ...documentData.value })
}
</script>
<template>
  <form @submit.prevent="submitForm" class="leave-need-confirm">
    <div class="w3-card-2 my-4 rounded-md border border-solid p-4">
      <h3>回覆</h3>
      <table class="w3-table">
        <tbody>
          <tr>
            <th class="align-middle md:w-1/3" style="width: 20rem">動作</th>
            <td>
              <div class="container flex flex-wrap">
                <input
                  type="radio"
                  id="sign_action_sign"
                  value="sign"
                  required="required"
                  class="hidden"
                  v-model="documentData.sign_action"
                />
                <label
                  for="sign_action_sign"
                  class="m-2 cursor-pointer rounded-md border border-solid border-scp-royal-blue px-4 py-1 text-scp-royal-blue"
                  :class="{ 'bg-scp-royal-blue text-white': documentData.sign_action === 'sign' }"
                >
                  簽核
                </label>
                <input
                  type="radio"
                  id="sign_action_return"
                  value="return"
                  required="required"
                  class="hidden"
                  v-model="documentData.sign_action"
                />
                <label
                  for="sign_action_return"
                  class="m-2 cursor-pointer rounded-md border border-solid border-scp-royal-blue px-4 py-1 text-scp-royal-blue"
                  :class="{ 'bg-scp-royal-blue text-white': documentData.sign_action === 'return' }"
                >
                  退回
                </label>
                <input
                  type="radio"
                  id="sign_action_forward"
                  value="forward"
                  required="required"
                  class="hidden"
                  v-model="documentData.sign_action"
                />
                <label
                  for="sign_action_forward"
                  class="m-2 cursor-pointer rounded-md border border-solid border-scp-royal-blue px-4 py-1 text-scp-royal-blue"
                  :class="{
                    'bg-scp-royal-blue text-white': documentData.sign_action === 'forward'
                  }"
                >
                  轉發
                </label>
                <input
                  type="radio"
                  id="sign_action_branch"
                  value="branch"
                  required="required"
                  class="hidden"
                  v-model="documentData.sign_action"
                />
                <label
                  for="sign_action_branch"
                  class="m-2 cursor-pointer rounded-md border border-solid border-scp-royal-blue px-4 py-1 text-scp-royal-blue"
                  :class="{ 'bg-scp-royal-blue text-white': documentData.sign_action === 'branch' }"
                >
                  分會
                </label>
                <input
                  type="radio"
                  id="sign_action_referral"
                  value="referral"
                  required="required"
                  class="hidden"
                  v-model="documentData.sign_action"
                />
                <label
                  for="sign_action_referral"
                  class="m-2 cursor-pointer rounded-md border border-solid border-scp-royal-blue px-4 py-1 text-scp-royal-blue"
                  :class="{
                    'bg-scp-royal-blue text-white': documentData.sign_action === 'referral'
                  }"
                >
                  轉知
                </label>
              </div>
              <template v-if="['forward', 'branch', 'referral'].includes(documentData.sign_action)">
                <div v-for="(row, rowIndex) in documentData.target" :key="rowIndex">
                  <div class="flex items-end gap-2 md:flex-col">
                    <div class="flex w-full flex-1 gap-2 md:flex-col">
                      <BaseSelect
                        class="procedure-select flex-1"
                        :label="'部門'"
                        name="target[][step_name]"
                        :optionList="transformedUnitList"
                        :optionsSelected="row.step_name"
                        @select="getUnitStaffList($event, rowIndex)"
                        required
                      />
                      <BaseSelect
                        class="procedure-select flex-1"
                        :label="'人員'"
                        name="target[][signer_id]"
                        :optionList="row.signer_list"
                        :optionsSelected="row.signer_id"
                        @select="checkUnitStaff($event, rowIndex)"
                        required
                      />
                    </div>
                    <button
                      v-if="documentData.target.length > 1"
                      class="h-[45px] rounded-md border border-red-400 bg-white px-3 tracking-wider text-red-400"
                      @click.prevent="deleteTarget(rowIndex)"
                    >
                      <i class="icon-trash text-2xl"></i>
                    </button>
                  </div>
                </div>
              </template>
              <div v-if="documentData.sign_action === 'referral'" class="mt-4">
                <button
                  class="w-full rounded-md border bg-scp-cyan-green p-3 font-bold tracking-widest text-white"
                  @click.prevent="addTarget"
                >
                  增加收件人
                </button>
              </div>
            </td>
          </tr>
          <tr>
            <th class="align-middle">
              <span class="mr-1">備註</span>
              <span class="text-sm text-red-400">*</span>
            </th>
            <td>
              <textarea
                name="suggestion"
                id="suggestion"
                row="5"
                class="textarea w-full border border-gray-300"
                v-model="documentData.suggestion"
                required
              ></textarea>
            </td>
          </tr>
          <tr>
            <th>文件上傳</th>
            <td style="padding-top: 0">
              <div class="w-1/2">
                <BaseFileUpload
                  :readonly="false"
                  :showUploadedFile="false"
                  :canDeleteProjectFile="false"
                  :acceptFileType="'application/pdf'"
                  class="col-span-2"
                  :projectFileList="totalReportFileList"
                  :maxFileNum="3"
                  @uploadFile="documentData.upload_files = $event"
                  @deletePreviewFile="handleDeletePreviewFile"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex items-center justify-end gap-3 py-3">
      <BackButton :hasForm="true" :returnPath="'/documents'" />
      <button type="submit" class="btn btn-success text-xl text-white">
        <SendOutlined />
        送出
      </button>
      <button
        v-if="
          currentUser.id === documentShowInfo.applicant.id && documentShowInfo.state === 'approving'
        "
        class="btn bg-red-500 text-xl text-white"
        @click.prevent="submitForm('cancel')"
      >
        取消公文
      </button>
    </div>
  </form>
</template>
<style lang="scss" scoped></style>
